import React from 'react';
import Menu from 'js/components/elements/menu/Menu';
import styles from './SdkApplicationActions.module.scss';
import { SdkApplication } from '@nexla/@types';
import { useSdkApplicationProcesses } from 'new/components/settings/SdkApplications/useSdkApplicationProcesses';
import { useModals } from 'new/hooks/useModals';
import { SdkApplicationModal } from 'new/components/settings/SdkApplications/SdkApplicationModal';
import { UpdateSdkApplicationPayload } from 'js/actions/sdkApplications';
import { ReactComponent as EditFileIcon } from 'new/components/icons/editFile.svg';
import { ReactComponent as RefreshIcon } from 'new/components/icons/refresh.svg';
import { ReactComponent as PauseIcon } from 'new/components/icons/fi-rr-pause.svg';
import { ReactComponent as PlayIcon } from 'new/components/icons/fi-rr-play.svg';
import { ReactComponent as TrashIcon } from 'new/components/icons/fi-rr-trash.svg';

type Props = {
  row: SdkApplication;
  update(id: number, data: UpdateSdkApplicationPayload): Promise<void>;
  remove(id: number): Promise<void>;
  pause(id: number): Promise<void>;
  activate(id: number): Promise<void>;
  rotateLinkedKeys(appId: number): Promise<void>;
};

export function SdkApplicationActions({
  row,
  update,
  remove,
  pause,
  activate,
  rotateLinkedKeys,
}: Props) {
  const { openModal } = useModals();
  const { isPausing, isActivating, isDeleting, isRotating } = useSdkApplicationProcesses(row);

  if (!row?.id) return null;

  const status = (row.status ?? 'ACTIVE').toUpperCase();
  const isActive = status === 'ACTIVE';

  const onClickEdit = () => {
    openModal(SdkApplicationModal.modalConfig.name, {
      mode: 'edit',
      app: row,
      updateApp: update,
    });
  };

  const menuItems: any[] = [
    {
      type: 'action',
      label: 'Edit',
      icon: <EditFileIcon className={styles.menuIcon} />,
      onClick: onClickEdit,
    },
    {
      type: 'dialog',
      label: isRotating ? 'Rotating...' : 'Rotate Keys',
      icon: <RefreshIcon className={styles.menuIcon} />,
      disable: isRotating,
      dialogMessage: `Rotate all service keys for "${row.name}"? Existing keys will be invalidated.`,
      discardLabel: 'Cancel',
      confirmLabel: 'Rotate',
      onClick: () => rotateLinkedKeys(row.id),
    },
  ];

  if (isActive) {
    menuItems.push({
      type: 'dialog',
      label: isPausing ? 'Pausing...' : 'Pause',
      icon: <PauseIcon className={styles.menuIcon} />,
      disable: isPausing,
      dialogMessage: `Pause "${row.name}"? Active credentials from this app will stop working.`,
      discardLabel: 'Cancel',
      confirmLabel: 'Pause',
      onClick: () => pause(row.id),
    });
  } else {
    menuItems.push({
      type: 'dialog',
      label: isActivating ? 'Activating...' : 'Activate',
      icon: <PlayIcon className={styles.menuIcon} />,
      disable: isActivating,
      dialogMessage: `Activate "${row.name}"?`,
      discardLabel: 'Cancel',
      confirmLabel: 'Activate',
      onClick: () => activate(row.id),
    });
  }

  menuItems.push({
    type: 'dialog',
    label: isDeleting ? 'Deleting...' : 'Delete',
    icon: <TrashIcon className={styles.menuIconError} />,
    color: 'error',
    disable: isDeleting,
    dialogMessage: `Delete "${row.name}"? This action cannot be undone.`,
    discardLabel: 'Cancel',
    confirmLabel: 'Delete',
    onClick: () => remove(row.id),
  });

  return <Menu items={menuItems} />;
}
